import MD5 from 'crypto-js/md5';
import axios from 'axios';
import * as jose from 'jose';
import { API_URLS } from '@/utils/apiConfig';
import { PUBLIC_KEY } from '/abc.js';

const { first: FIRST_API_URL, second: SECOND_API_URL } = API_URLS;

let errorPageCreated = false;

const AUTH_STATUS = {
    INIT: '初始化',
    API_CHECKING: '接口授权验证中',
    JWT_VERIFYING: 'JWT验证中',
    SIGN_VERIFYING: '响应签名验证中',
    SIGN_UNSUPPORTED: '当前环境不支持验签，已跳过签名验证',
    SIGN_SUCCESS: '签名验证成功',
    SIGN_FAIL: '签名验证失败',
    AUTH_SUCCESS: '授权成功',
    AUTH_FAIL: '授权失败',
    CACHE_HIT: '命中缓存',
    CACHE_EXPIRED: '缓存过期',
    BYPASS_SIGN_ONLY: '仅跳过签名验证，接口授权仍正常执行'
};

function logStatus(status, extra = null) {
    const time = new Date().toLocaleString();
    if (extra) {
        console.log(`%c[授权状态] ${status} - ${time}`, 'color:#1677ff;font-weight:bold;', extra);
    } else {
        console.log(`%c[授权状态] ${status} - ${time}`, 'color:#1677ff;font-weight:bold;');
    }
}

function isHttpProtocol() {
    return window.location.protocol === 'http:';
}

function isMobileBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    return /micromessenger|qq|wxwork|wechatwork|dingtalk/.test(ua);
}

function supportsSubtleCrypto() {
    return !!(window.crypto && window.crypto.subtle);
}

function supportsJose() {
    return typeof TextEncoder !== 'undefined' &&
        typeof TextDecoder !== 'undefined' &&
        !!window.crypto;
}

function canStrictVerifySign() {
    if (typeof window === 'undefined') return false;
    if (typeof atob === 'undefined') return false;
    if (typeof TextEncoder === 'undefined') return false;
    if (!supportsSubtleCrypto()) return false;
    return true;
}

function check() {
    if (!PUBLIC_KEY) {
        console.error('❌ 公钥未配置，授权模块初始化失败');
        showErrorPage(
            1007,
            '公钥配置缺失，无法进行授权验证。请检查配置文件是否存在。',
            '配置错误'
        );
        throw new Error('PUBLIC_KEY is not configured');
    }
}

check();

function base64Decode(base64) {
    const cleanBase64 = base64.replace(/^data:[^;]+;base64,/, '');
    const standardBase64 = cleanBase64
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(cleanBase64.length + (4 - cleanBase64.length % 4) % 4, '=');

    try {
        const binaryString = atob(standardBase64);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return new TextDecoder().decode(bytes);
    } catch (error) {
        return cleanBase64;
    }
}

function base64UrlDecodeToJson(input) {
    const str = input
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(input.length + (4 - input.length % 4) % 4, '=');

    const binary = atob(str);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    const json = typeof TextDecoder !== 'undefined'
        ? new TextDecoder().decode(bytes)
        : decodeURIComponent(escape(binary));

    return JSON.parse(json);
}

function cleanPublicKey(publicKey) {
    if (!publicKey) return '';
    return publicKey
        .replace(/-----BEGIN PUBLIC KEY-----/g, '')
        .replace(/-----END PUBLIC KEY-----/g, '')
        .replace(/\n/g, '')
        .replace(/\r/g, '')
        .replace(/\s+/g, '')
        .trim();
}

function showErrorPage(errorCode, errorMessage, errorType = 'Runtime Exception') {
    if (errorPageCreated) return;

    errorPageCreated = true;
    document.body.innerHTML = '';

    const container = document.createElement('div');
    container.style.cssText = `
        position:fixed;
        inset:0;
        background:rgba(240,242,245,.96);
        display:flex;
        justify-content:center;
        align-items:center;
        z-index:999999;
        font-family:PingFang SC,Microsoft YaHei,Arial,sans-serif;
        padding:20px;
        box-sizing:border-box;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
        width:100%;
        max-width:520px;
        background:#fff;
        border:1px solid #e8e8e8;
        box-shadow:0 8px 32px rgba(0,0,0,.08);
        padding:38px 30px;
        box-sizing:border-box;
        text-align:center;
    `;

    panel.innerHTML = `
        <div style="font-size:20px;font-weight:600;color:#1f1f1f;margin-bottom:8px;">发生错误</div>
        <div style="font-size:13px;color:#999;margin-bottom:24px;">An error occurred</div>

        <div style="background:#f8f9fa;border:1px solid #e8e8e8;padding:16px;text-align:left;margin-bottom:22px;">
            <div style="font-size:12px;color:#999;margin-bottom:8px;">错误信息：</div>
            <div style="font-size:14px;color:#333;line-height:1.6;word-break:break-all;">${String(errorMessage)}</div>
        </div>

        <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:12px;text-align:left;margin-bottom:24px;">
            <div>
                <div style="font-size:12px;color:#999;">错误代码</div>
                <div style="font-size:13px;color:#ff4d4f;font-weight:600;">${errorCode}</div>
            </div>
            <div>
                <div style="font-size:12px;color:#999;">错误类型</div>
                <div style="font-size:13px;color:#fa8c16;font-weight:600;">${errorType}</div>
            </div>
            <div>
                <div style="font-size:12px;color:#999;">发生时间</div>
                <div style="font-size:13px;color:#1677ff;font-weight:600;">${new Date().toLocaleString()}</div>
            </div>
            <div>
                <div style="font-size:12px;color:#999;">影响范围</div>
                <div style="font-size:13px;color:#52c41a;font-weight:600;">当前会话</div>
            </div>
        </div>

        <button id="auth-error-refresh" style="
            height:40px;
            padding:0 28px;
            border:0;
            border-radius:4px;
            background:#1677ff;
            color:#fff;
            cursor:pointer;
            font-size:14px;
        ">刷新页面</button>
    `;

    container.appendChild(panel);
    document.body.appendChild(container);

    const btn = document.getElementById('auth-error-refresh');
    if (btn) {
        btn.onclick = () => location.reload();
    }
}

function getErrorMessage(result) {
    if (result.error) return result.error;
    if (result.authResult && result.authResult.message) return result.authResult.message;
    if (!result.signatureValid && result.signatureError) return `签名验证失败: ${result.signatureError}`;
    if (result.expirationCheck && !result.expirationCheck.isValid) {
        return `令牌过期: ${result.expirationCheck.error || '令牌已失效'}`;
    }
    return '未知错误';
}

function getErrorCode(result) {
    if (result.authCode) return result.authCode;
    if (result.authResult && result.authResult.code) return result.authResult.code;
    if (!result.signatureValid) return 1003;
    if (result.expirationCheck && !result.expirationCheck.isValid) {
        if (result.expirationCheck.expired) return 1004;
        if (result.expirationCheck.notYetValid) return 1005;
    }
    return 1000;
}

function pemToArrayBuffer(pem) {
    const b64 = pem
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\s+/g, '');

    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes.buffer;
}

let importedPublicKeyPromise = null;

async function importPublicKey() {
    if (importedPublicKeyPromise) return importedPublicKeyPromise;

    importedPublicKeyPromise = window.crypto.subtle.importKey(
        'spki',
        pemToArrayBuffer(PUBLIC_KEY),
        {
            name: 'RSASSA-PKCS1-v1_5',
            hash: 'SHA-256'
        },
        false,
        ['verify']
    );

    return importedPublicKeyPromise;
}

function base64ToUint8Array(base64) {
    try {
        let cleaned = String(base64)
            .replace(/[\n\r\s]/g, '')
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        cleaned = cleaned.replace(/[^A-Za-z0-9+/=]/g, '');

        const pad = cleaned.length % 4;
        if (pad) cleaned += '='.repeat(4 - pad);

        const binary = atob(cleaned);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return bytes;
    } catch (error) {
        console.error('Base64解码失败:', error.message);
        throw error;
    }
}

function buildSignString(data) {
    const { sign, ...restData } = data;

    const keys = Object.keys(restData)
        .filter(k => k !== 'sign' && k !== 'key')
        .filter(k => restData[k] !== '' && restData[k] !== null && restData[k] !== undefined)
        .sort();

    return keys
        .map(k => {
            const value = restData[k];
            if (typeof value === 'number') {
                return `${k}=${value}`;
            }
            return `${k}=${encodeURIComponent(value)}`;
        })
        .join('&');
}

export async function verifyResponseSign(responseData) {
    if (!responseData || typeof responseData !== 'object') {
        console.warn('响应数据为空，跳过签名验证');
        logStatus(AUTH_STATUS.SIGN_UNSUPPORTED);
        return {
            supported: false,
            valid: true,
            skipped: true,
            reason: 'EMPTY_RESPONSE'
        };
    }

    if (!responseData.sign) {
        console.warn('响应中没有 sign 字段，跳过签名验证');
        logStatus(AUTH_STATUS.SIGN_UNSUPPORTED, { reason: 'NO_SIGN_FIELD' });
        return {
            supported: false,
            valid: true,
            skipped: true,
            reason: 'NO_SIGN_FIELD'
        };
    }

    if (!canStrictVerifySign()) {
        console.warn('当前浏览器不支持严格 RSA 验签，已跳过签名验证，但接口授权判断正常执行');
        logStatus(AUTH_STATUS.BYPASS_SIGN_ONLY);
        return {
            supported: false,
            valid: true,
            skipped: true,
            reason: 'SIGN_API_UNSUPPORTED'
        };
    }

    try {
        logStatus(AUTH_STATUS.SIGN_VERIFYING);

        const sign = responseData.sign;

        if (!sign || typeof sign !== 'string') {
            console.error('签名格式无效:', sign);
            logStatus(AUTH_STATUS.SIGN_FAIL);
            return {
                supported: true,
                valid: false,
                skipped: false,
                reason: 'INVALID_SIGN_FORMAT'
            };
        }

        const cleanedSign = sign.replace(/[\n\r\s]/g, '');
        const signStr = buildSignString(responseData);

        const publicKey = await importPublicKey();
        const signature = base64ToUint8Array(cleanedSign);
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(signStr);

        const isValid = await window.crypto.subtle.verify(
            'RSASSA-PKCS1-v1_5',
            publicKey,
            signature,
            dataBuffer
        );

        if (isValid) {
            logStatus(AUTH_STATUS.SIGN_SUCCESS);
        } else {
            logStatus(AUTH_STATUS.SIGN_FAIL);
        }

        return {
            supported: true,
            valid: isValid,
            skipped: false,
            reason: null
        };
    } catch (error) {
        console.error('签名验证异常:', error.message);

        if (!canStrictVerifySign()) {
            logStatus(AUTH_STATUS.BYPASS_SIGN_ONLY);
            return {
                supported: false,
                valid: true,
                skipped: true,
                reason: error.message
            };
        }

        logStatus(AUTH_STATUS.SIGN_FAIL);
        return {
            supported: true,
            valid: false,
            skipped: false,
            reason: error.message
        };
    }
}

class JWTVerifier {
    constructor(publicKey) {
        this.publicKey = this.formatPublicKey(publicKey);
        this.supportsJose = supportsJose();
    }

    formatPublicKey(rawKey) {
        const cleaned = cleanPublicKey(rawKey);
        const lines = [];

        for (let i = 0; i < cleaned.length; i += 64) {
            lines.push(cleaned.substring(i, i + 64));
        }

        return `-----BEGIN PUBLIC KEY-----\n${lines.join('\n')}\n-----END PUBLIC KEY-----`;
    }

    fallbackVerify(token) {
        try {
            if (!token) {
                return {
                    valid: false,
                    error: 'Token is required',
                    decoded: null
                };
            }

            console.warn('使用降级 JWT 验证方法：只做格式和时间判断，不做 RSA 签名验签');
            logStatus('JWT降级验证');

            const parts = token.split('.');
            if (parts.length !== 3) {
                return {
                    valid: false,
                    error: 'Invalid token format',
                    decoded: null
                };
            }

            const header = base64UrlDecodeToJson(parts[0]);
            const payload = base64UrlDecodeToJson(parts[1]);
            const now = Math.floor(Date.now() / 1000);

            if (payload.exp && payload.exp < now) {
                return {
                    valid: false,
                    error: 'Token expired',
                    errorType: 'EXPIRED',
                    header,
                    payload,
                    signature: parts[2],
                    decoded: { header, payload, signature: parts[2] }
                };
            }

            if (payload.nbf && payload.nbf > now) {
                return {
                    valid: false,
                    error: 'Token not yet valid',
                    errorType: 'NOT_YET_VALID',
                    header,
                    payload,
                    signature: parts[2],
                    decoded: { header, payload, signature: parts[2] }
                };
            }

            return {
                valid: true,
                error: null,
                header,
                payload,
                signature: parts[2],
                decoded: { header, payload, signature: parts[2] }
            };
        } catch (error) {
            return {
                valid: false,
                error: error.message,
                decoded: null
            };
        }
    }

    async verify(token) {
        try {
            if (!token) {
                return {
                    valid: false,
                    error: 'Token is required',
                    decoded: null
                };
            }

            logStatus(AUTH_STATUS.JWT_VERIFYING);

            if (!this.supportsJose) {
                console.warn('当前环境不支持 JOSE 严格验证，使用降级 JWT 验证');
                return this.fallbackVerify(token);
            }

            const publicKey = await jose.importSPKI(this.publicKey, 'RS256');
            const { payload, protectedHeader } = await jose.jwtVerify(token, publicKey, {
                algorithms: ['RS256'],
                clockTolerance: 30
            });

            return {
                valid: true,
                error: null,
                header: protectedHeader,
                payload,
                signature: token.split('.')[2]
            };
        } catch (error) {
            console.error('JWT验证失败:', error.message);

            if (isHttpProtocol() || isMobileBrowser()) {
                console.warn('当前环境可能不支持严格 JWT 验证，尝试降级验证');
                const fallbackResult = this.fallbackVerify(token);
                if (fallbackResult.valid) {
                    return fallbackResult;
                }
            }

            let errorType = 'UNKNOWN';

            if (error.code === 'ERR_JWT_EXPIRED') {
                errorType = 'EXPIRED';
            } else if (error.code === 'ERR_JWS_INVALID') {
                errorType = 'INVALID';
            } else if (error.code === 'ERR_JWT_CLAIM_VALIDATION_FAILED') {
                errorType = 'CLAIM_VALIDATION_FAILED';
            }

            return {
                valid: false,
                error: error.message,
                errorType,
                decoded: null
            };
        }
    }

    decode(token) {
        try {
            if (!token) {
                return {
                    valid: false,
                    error: 'Token is required'
                };
            }

            const parts = token.split('.');
            if (parts.length !== 3) {
                return {
                    valid: false,
                    error: 'Invalid token format'
                };
            }

            if (this.supportsJose) {
                return {
                    valid: true,
                    payload: jose.decodeJwt(token),
                    header: jose.decodeProtectedHeader(token),
                    signature: parts[2]
                };
            }

            return {
                valid: true,
                payload: base64UrlDecodeToJson(parts[1]),
                header: base64UrlDecodeToJson(parts[0]),
                signature: parts[2]
            };
        } catch (error) {
            return {
                valid: false,
                error: error.message
            };
        }
    }

    checkExpiration(token) {
        try {
            const decoded = this.decode(token);

            if (!decoded.valid || !decoded.payload) {
                return {
                    expired: true,
                    error: 'Invalid token format',
                    isValid: false
                };
            }

            const payload = decoded.payload;
            const now = Math.floor(Date.now() / 1000);
            const clockTolerance = 30;

            if (payload.exp && payload.exp < now - clockTolerance) {
                return {
                    expired: true,
                    expiredAt: new Date(payload.exp * 1000),
                    remainingSeconds: 0,
                    error: `Token expired at ${new Date(payload.exp * 1000)}`,
                    isValid: false
                };
            }

            if (payload.nbf && payload.nbf > now + clockTolerance) {
                return {
                    expired: false,
                    notYetValid: true,
                    becomesValidAt: new Date(payload.nbf * 1000),
                    error: `Token not valid until ${new Date(payload.nbf * 1000)}`,
                    isValid: false
                };
            }

            if (payload.iat && payload.iat > now + clockTolerance) {
                return {
                    expired: false,
                    futureIssued: true,
                    error: 'Token issued in the future',
                    isValid: false
                };
            }

            return {
                expired: false,
                notYetValid: false,
                remainingSeconds: payload.exp ? payload.exp - now : Infinity,
                expiresAt: payload.exp ? new Date(payload.exp * 1000) : null,
                notBefore: payload.nbf ? new Date(payload.nbf * 1000) : null,
                issuedAt: payload.iat ? new Date(payload.iat * 1000) : null,
                isValid: true
            };
        } catch (error) {
            return {
                expired: true,
                error: error.message,
                isValid: false
            };
        }
    }

    async fullVerify(token) {
        const signatureVerification = await this.verify(token);

        if (!signatureVerification.valid) {
            return {
                ...signatureVerification,
                overallValid: false,
                expirationCheck: null
            };
        }

        const expirationCheck = this.checkExpiration(token);

        return {
            ...signatureVerification,
            expirationCheck,
            overallValid: signatureVerification.valid && expirationCheck.isValid
        };
    }
}

const jwtVerifier = new JWTVerifier(PUBLIC_KEY);

class TokenCacheManager {
    constructor() {
        this.CACHE_KEY = 'jwt_token_cache';
        this.SECOND_API_CACHE_KEY = 'second_api_cache';
        this.SECOND_API_CACHE_DURATION = 2 * 60 * 60 * 1000;
    }

    getCachedToken() {
        try {
            const cacheStr = localStorage.getItem(this.CACHE_KEY);
            if (!cacheStr) return null;

            const cache = JSON.parse(cacheStr);

            if (cache.expiresAt && new Date(cache.expiresAt) <= new Date()) {
                logStatus(AUTH_STATUS.CACHE_EXPIRED);
                this.clearCache();
                return null;
            }

            logStatus(AUTH_STATUS.CACHE_HIT);
            return cache;
        } catch (error) {
            console.error('读取缓存失败:', error);
            this.clearCache();
            return null;
        }
    }

    getSecondApiCache() {
        try {
            const cacheStr = localStorage.getItem(this.SECOND_API_CACHE_KEY);
            if (!cacheStr) return null;

            const cache = JSON.parse(cacheStr);

            if (cache.cachedAt) {
                const cachedTime = new Date(cache.cachedAt).getTime();
                const now = Date.now();
                const age = now - cachedTime;

                if (age >= this.SECOND_API_CACHE_DURATION) {
                    localStorage.removeItem(this.SECOND_API_CACHE_KEY);
                    return null;
                }

                return cache.result;
            }

            return null;
        } catch (error) {
            console.error('读取第二个接口缓存失败:', error);
            localStorage.removeItem(this.SECOND_API_CACHE_KEY);
            return null;
        }
    }

    saveSecondApiCache(result) {
        try {
            const cache = {
                result,
                cachedAt: new Date().toISOString(),
                expiresAt: new Date(Date.now() + this.SECOND_API_CACHE_DURATION).toISOString()
            };

            localStorage.setItem(this.SECOND_API_CACHE_KEY, JSON.stringify(cache));
        } catch (error) {
            console.error('保存第二个接口缓存失败:', error);
        }
    }

    shouldCallSecondApi() {
        return !this.getSecondApiCache();
    }

    saveTokenToCache(tokenData) {
        try {
            const cache = {
                token: tokenData.token,
                payload: tokenData.payload,
                expiresAt: tokenData.expiresAt,
                cachedAt: new Date().toISOString()
            };

            localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
        } catch (error) {
            console.error('保存缓存失败:', error);
        }
    }

    clearCache() {
        localStorage.removeItem(this.CACHE_KEY);
        localStorage.removeItem(this.SECOND_API_CACHE_KEY);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_token_expiry');
    }
}

const cacheManager = new TokenCacheManager();

async function verifyAuthorization(domain, jwtPayload, forceRefresh = false, isInitial = false) {
    try {
        if (!forceRefresh) {
            const cachedResult = cacheManager.getSecondApiCache();
            if (cachedResult) {
                logStatus('使用第二个接口缓存结果');
                return cachedResult;
            }
        }

        logStatus(AUTH_STATUS.API_CHECKING);

        const key = jwtPayload?.info?.key;
        const time = jwtPayload?.info?.time;

        if (!key || !time) {
            console.warn('JWT中缺少key或time字段:', jwtPayload);

            const result = {
                authorized: false,
                code: 1001,
                message: 'JWT令牌中缺少必要字段',
                data: null
            };

            if (isInitial) {
                showErrorPage(result.code, result.message, 'JWT令牌错误');
            }

            return result;
        }

        const requestData = {
            domain,
            key,
            time
        };

        const response = await axios.post(base64Decode(SECOND_API_URL), requestData, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            timeout: 10000
        });

        const responseData = response.data;

        if (responseData.code === 1000) {
            const signResult = await verifyResponseSign(responseData);

            if (!signResult.valid) {
                const result = {
                    authorized: false,
                    code: 1003,
                    message: '签名验证失败',
                    data: responseData,
                    signResult
                };

                if (isInitial) {
                    showErrorPage(result.code, result.message, '签名验证失败');
                }

                return result;
            }

            if (signResult.skipped) {
                console.warn('当前环境未执行严格签名验证，但接口 code=1000，授权判断通过');
                logStatus(AUTH_STATUS.BYPASS_SIGN_ONLY, signResult);
            }

            const result = {
                authorized: true,
                code: responseData.code,
                message: responseData.message,
                data: responseData,
                signResult
            };

            cacheManager.saveSecondApiCache(result);
            logStatus(AUTH_STATUS.AUTH_SUCCESS, result);

            return result;
        }

        const result = {
            authorized: false,
            code: responseData.code || 1002,
            message: responseData.message || '授权验证失败',
            data: responseData
        };

        logStatus(AUTH_STATUS.AUTH_FAIL, result);

        if (isInitial) {
            showErrorPage(result.code, result.message, '授权验证失败');
        }

        return result;
    } catch (error) {
        console.error('❌ 授权验证请求失败:', error.message);

        const cachedResult = !forceRefresh ? cacheManager.getSecondApiCache() : null;
        if (cachedResult) {
            console.warn('网络错误，使用第二个接口缓存结果');
            return cachedResult;
        }

        const result = {
            authorized: false,
            code: 1004,
            message: `授权验证请求失败: ${error.message}`,
            error
        };

        if (isInitial) {
            showErrorPage(result.code, result.message, '网络错误');
        }

        return result;
    }
}

function getResponseMsg(data) {
    if (!data) return '';
    return data.msg || data.message || data.error || data.errmsg || '';
}

function getResponseCode(data) {
    if (!data) return 1008;
    return data.status || data.code || data.error_code || 1008;
}

function isBusinessErrorResponse(data) {
    if (!data || typeof data !== 'object') return false;

    const msg = getResponseMsg(data);
    const code = getResponseCode(data);

    if (msg) return true;
    if (code && code !== 1000 && code !== 200) return true;

    return false;
}

function isFirstApiNetworkDead(error) {
    // 接口完全请求不通：原站死了、跨域失败、超时、断网、DNS错误等
    if (!error.response) return true;

    return false;
}

async function performInitialVerification(domain, isInitial = true) {
    try {
        logStatus(AUTH_STATUS.INIT);

        const encryptedKey = MD5(cleanPublicKey(PUBLIC_KEY)).toString();

        const requestData = {
            domain,
            key: encryptedKey
        };

        const response = await axios.post(base64Decode(FIRST_API_URL), requestData, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            timeout: 10000
        });

        const responseData = response.data;

        // 第一个接口有返回，但是返回的是业务异常，例如：
        // { msg: "授权不存在", status: 400 }
        // { message: "授权不存在", code: 400 }
        if (isBusinessErrorResponse(responseData)) {
            cacheManager.clearCache();

            const result = {
                success: false,
                authorized: false,
                error: getResponseMsg(responseData) || '授权验证失败',
                authCode: getResponseCode(responseData),
                data: responseData
            };

            if (isInitial) {
                showErrorPage(
                    result.authCode,
                    result.error,
                    '授权验证失败'
                );
            }

            return result;
        }

        // 第一个接口没有正常返回 token
        if (!responseData || typeof responseData !== 'string') {
            cacheManager.clearCache();

            const result = {
                success: false,
                authorized: false,
                error: '第一个接口返回异常',
                authCode: 1008,
                data: responseData
            };

            if (isInitial) {
                showErrorPage(
                    result.authCode,
                    result.error,
                    '接口返回异常'
                );
            }

            return result;
        }

        const token = responseData;
        const verification = await jwtVerifier.fullVerify(token);

        if (!verification.payload || !verification.overallValid) {
            cacheManager.clearCache();

            const result = {
                success: false,
                authorized: false,
                error: 'Token validation failed',
                signatureValid: verification.valid,
                expirationValid: verification.expirationCheck?.isValid,
                signatureError: verification.error,
                expirationError: verification.expirationCheck?.error,
                verification
            };

            if (isInitial) {
                showErrorPage(
                    getErrorCode(verification),
                    getErrorMessage(verification),
                    'JWT令牌验证失败'
                );
            }

            return result;
        }

        const authResult = await verifyAuthorization(domain, verification.payload, true, isInitial);

        if (!authResult.authorized) {
            cacheManager.clearCache();

            return {
                success: false,
                authorized: false,
                error: authResult.message,
                authCode: authResult.code,
                verification,
                authResult
            };
        }

        const expiresAt = verification.payload?.exp
            ? new Date(verification.payload.exp * 1000)
            : null;

        cacheManager.saveTokenToCache({
            token,
            payload: verification.payload,
            expiresAt
        });

        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_token_expiry', verification.payload?.exp || '');

        return {
            success: true,
            authorized: true,
            fromCache: false,
            secondApiCached: false,
            token,
            payload: verification.payload,
            verification,
            authResult,
            expiresAt
        };
    } catch (error) {
        console.error('❌ 第一个接口请求错误:', error.message);

        // 关键逻辑：
        // 第一个接口完全请求不通，说明原站可能死了，直接放行，不弹窗，不验签
        if (isFirstApiNetworkDead(error)) {
            console.warn('第一个接口请求不通，已跳过授权验证，允许正常访问');

            return {
                success: true,
                authorized: true,
                bypass: true,
                bypassReason: 'FIRST_API_NETWORK_DEAD',
                error: error.message
            };
        }

        // 有 HTTP 返回，说明接口是通的，只是返回了异常状态
        // 例如 400 / 403 / 500，并且里面有 msg/message
        const responseData = error.response?.data;

        const result = {
            success: false,
            authorized: false,
            error: getResponseMsg(responseData) || error.message || '第一个接口返回异常',
            authCode: getResponseCode(responseData),
            data: responseData
        };

        if (isInitial) {
            showErrorPage(
                result.authCode,
                result.error,
                '授权验证失败'
            );
        }

        return result;
    }
}

async function refreshTokenSilently(domain) {
    try {
        const result = await performInitialVerification(domain, false);

        if (result.success && result.authorized) {
            logStatus('静默刷新成功');
        } else {
            console.warn('静默刷新失败，但不影响当前会话');
        }

        return result;
    } catch (error) {
        console.error('静默刷新出错:', error);
        return {
            success: false,
            error: error.message
        };
    }
}

async function refreshTokenOnFailure(showError = false) {
    try {
        const domain = window.location.hostname;

        // 先静默重新请求令牌
        const result = await performInitialVerification(domain, showError);

        if (result.success && result.authorized) {
            logStatus('令牌刷新成功', result);
            return result;
        }

        console.warn('令牌刷新失败:', result);

        return {
            success: false,
            authorized: false,
            error: result.error || '刷新令牌失败',
            needUserAction: showError
        };
    } catch (error) {
        console.error('刷新令牌失败:', error);

        if (showError) {
            showErrorPage(1010, `刷新令牌失败: ${error.message}`, '令牌刷新失败');
        }

        return {
            success: false,
            authorized: false,
            error: '刷新令牌失败',
            needUserAction: showError
        };
    }
}

export async function verifyDomainAndValidateToken() {
    const domain = window.location.hostname;

    const cachedToken = cacheManager.getCachedToken();

    if (cachedToken && cachedToken.token) {
        const expirationCheck = jwtVerifier.checkExpiration(cachedToken.token);

        // 缓存 token 有效
        if (expirationCheck.isValid) {
            const needSecondApi = cacheManager.shouldCallSecondApi();

            if (!needSecondApi) {
                return {
                    success: true,
                    authorized: true,
                    fromCache: true,
                    secondApiCached: true,
                    token: cachedToken.token,
                    payload: cachedToken.payload,
                    expiresAt: cachedToken.expiresAt
                };
            }

            const authResult = await verifyAuthorization(domain, cachedToken.payload, true, false);

            if (!authResult.authorized) {
                cacheManager.clearCache();

                // 授权失败，先重新请求令牌，不弹窗
                const refreshResult = await refreshTokenOnFailure(false);

                if (refreshResult.success && refreshResult.authorized) {
                    return refreshResult;
                }

                // 重新请求也失败，再走正式初始化，可按你原来的逻辑弹错误页
                return await performInitialVerification(domain, true);
            }

            return {
                success: true,
                authorized: true,
                fromCache: true,
                secondApiCached: false,
                token: cachedToken.token,
                payload: cachedToken.payload,
                expiresAt: cachedToken.expiresAt,
                authResult
            };
        }

        // token 过期：不要弹窗，先清缓存并重新请求令牌
        logStatus('令牌已过期，开始重新请求令牌', expirationCheck);
        cacheManager.clearCache();

        const refreshResult = await refreshTokenOnFailure(false);

        if (refreshResult.success && refreshResult.authorized) {
            return refreshResult;
        }

        // 重新请求失败后，才允许弹错误页
        return await performInitialVerification(domain, true);
    }

    // 没有缓存 token，正常首次请求
    return await performInitialVerification(domain, true);
}

async function verifyAuthorizationOnly(token) {
    check();

    try {
        const domain = window.location.hostname;
        const decoded = jwtVerifier.decode(token);

        if (!decoded.valid) {
            return {
                authorized: false,
                code: 1005,
                message: 'JWT解码失败',
                decoded
            };
        }

        return await verifyAuthorization(domain, decoded.payload, false, false);
    } catch (error) {
        console.error('授权验证异常:', error);
        return {
            authorized: false,
            code: 1006,
            message: `授权验证异常: ${error.message}`
        };
    }
}

export function startPeriodicVerification() {
    const intervalMs = 2 * 60 * 60 * 1000; // 2小时
    const NEXT_VERIFY_KEY = 'auth_next_verify_time';

    let timer = null;

    const getNextVerifyTime = () => {
        const saved = Number(localStorage.getItem(NEXT_VERIFY_KEY));

        if (saved && saved > Date.now()) {
            return saved;
        }

        const next = Date.now() + intervalMs;
        localStorage.setItem(NEXT_VERIFY_KEY, String(next));
        return next;
    };

    const runVerify = async () => {
        try {
            console.log(`%c[授权状态] 执行定时验证 - ${new Date().toLocaleString()}`, 'color:#1677ff;font-weight:bold;');

            const cachedToken = cacheManager.getCachedToken();

            if (cachedToken && cachedToken.payload) {
                const domain = window.location.hostname;
                const authResult = await verifyAuthorization(domain, cachedToken.payload, true, false);

                if (authResult.authorized) {
                    console.log('%c[授权状态] 定时验证成功', 'color:#52c41a;font-weight:bold;');
                } else {
                    console.warn('[授权状态] 定时验证失败，尝试静默刷新:', authResult.message);
                    cacheManager.clearCache();
                    await refreshTokenSilently(domain);
                }
            } else {
                console.log('[授权状态] 无有效JWT缓存，跳过定时验证');
            }
        } catch (error) {
            console.error('定时验证出错:', error);
        } finally {
            const next = Date.now() + intervalMs;
            localStorage.setItem(NEXT_VERIFY_KEY, String(next));

            console.log(
                `%c[授权状态] 下次验证时间：${new Date(next).toLocaleString()}`,
                'color:#1677ff;font-weight:bold;'
            );

            timer = setTimeout(runVerify, intervalMs);
        }
    };

    const start = () => {
        const nextVerifyTime = getNextVerifyTime();
        const delay = Math.max(nextVerifyTime - Date.now(), 1000);

        console.log(
            `%c[授权状态] 下次验证时间：${new Date(nextVerifyTime).toLocaleString()}`,
            'color:#1677ff;font-weight:bold;'
        );

        timer = setTimeout(runVerify, delay);
    };

    start();

    return timer;
}

export async function refreshSecondApiVerification() {
    try {
        const cachedToken = cacheManager.getCachedToken();

        if (!cachedToken || !cachedToken.payload) {
            return {
                success: false,
                error: 'No valid token cache'
            };
        }

        const domain = window.location.hostname;
        const authResult = await verifyAuthorization(domain, cachedToken.payload, true, false);

        if (authResult.authorized) {
            return {
                success: true,
                authorized: true,
                authResult
            };
        }

        const refreshResult = await refreshTokenOnFailure();

        if (refreshResult.success && refreshResult.authorized) {
            return {
                success: true,
                authorized: true,
                fromRefresh: true,
                authResult
            };
        }

        return {
            success: false,
            authorized: false,
            error: '手动刷新失败'
        };
    } catch (error) {
        console.error('手动刷新验证出错:', error);
        return {
            success: false,
            error: error.message
        };
    }
}

export async function initTokenVerification() {
    check();

    const result = await verifyDomainAndValidateToken();

    if (result.success && result.authorized) {
        logStatus(AUTH_STATUS.AUTH_SUCCESS, result);
    } else if (result.success && result.fromCache) {
        logStatus('使用缓存令牌成功', result);
    } else {
        logStatus(AUTH_STATUS.AUTH_FAIL, result);
    }

    return result;
}

export {
    jwtVerifier,
    cacheManager,
    verifyAuthorization,
    verifyAuthorizationOnly,
    showErrorPage,
    logStatus
};